/**
 * QuantumFlowSync.AI - Enhanced Admin Dashboard
 * Dashboard home with stats, activity feed, and quick actions
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, Activity, Ticket, CheckCircle, 
  Plus, Eye, UserPlus, TrendingUp, TrendingDown,
  Clock, Shield, AlertTriangle, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { PermissionGate } from '../../components/PermissionGate';

const EnhancedAdminDashboard = () => {
  const { apiRequest, employee } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [stats, setStats] = useState({
    total_employees: 0,
    active_sessions: 0,
    open_tickets: 0,
    pending_approvals: 0,
    employees_change: 0,
    sessions_change: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Permissions
  const canViewEmployees = usePermission('employees', '*', 'read');
  const canCreateEmployee = usePermission('employees', '*', 'create');
  const canViewAudit = usePermission('audit', 'logs', 'read');
  
  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiRequest('/api/admin/dashboard/stats');
      
      if (response && response.success && response.data) {
        setStats(response.data.stats || {
          total_employees: 0,
          active_sessions: 0,
          open_tickets: 0,
          pending_approvals: 0,
          employees_change: 0,
          sessions_change: 0
        });
        // Ensure recentActivity is always an array
        const activity = response.data.recent_activity;
        setRecentActivity(Array.isArray(activity) ? activity : []);
      } else {
        setError(response?.error || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  // Effect: Fetch data on mount
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Render activity icon
  const renderActivityIcon = (action) => {
    const icons = {
      login: <Users className="w-4 h-4" />,
      logout: <Users className="w-4 h-4" />,
      create_employee: <UserPlus className="w-4 h-4" />,
      update_employee: <Users className="w-4 h-4" />,
      assign_role: <Shield className="w-4 h-4" />,
      revoke_role: <Shield className="w-4 h-4" />
    };
    
    return icons[action] || <Activity className="w-4 h-4" />;
  };
  
  // Render activity color
  const getActivityColor = (result) => {
    const colors = {
      success: 'bg-green-100 text-green-600',
      denied: 'bg-red-100 text-red-600',
      failure: 'bg-yellow-100 text-yellow-600'
    };
    
    return colors[result] || 'bg-gray-100 text-gray-600';
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Welcome back, {employee?.name}
          </p>
        </div>
        
        <button
          onClick={fetchDashboardData}
          disabled={loading}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Employees */}
        <PermissionGate resource="employees" scope="*" action="read">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {stats.total_employees}
                </p>
                {stats.employees_change !== 0 && (
                  <div className={`flex items-center mt-2 text-xs ${
                    stats.employees_change > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stats.employees_change > 0 ? (
                      <TrendingUp className="w-3 h-3 mr-1" />
                    ) : (
                      <TrendingDown className="w-3 h-3 mr-1" />
                    )}
                    {Math.abs(stats.employees_change)}% from last month
                  </div>
                )}
              </div>
              <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </div>
        </PermissionGate>
        
        {/* Active Sessions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.active_sessions}
              </p>
              {stats.sessions_change !== 0 && (
                <div className={`flex items-center mt-2 text-xs ${
                  stats.sessions_change > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stats.sessions_change > 0 ? (
                    <TrendingUp className="w-3 h-3 mr-1" />
                  ) : (
                    <TrendingDown className="w-3 h-3 mr-1" />
                  )}
                  {Math.abs(stats.sessions_change)}% from last hour
                </div>
              )}
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
        
        {/* Open Tickets */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Open Tickets</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.open_tickets}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Requires attention
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
              <Ticket className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>
        
        {/* Pending Approvals */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending Approvals</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.pending_approvals}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Awaiting review
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity Feed */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recent Activity
              </h2>
              <PermissionGate resource="audit" scope="logs" action="read">
                <Link
                  to="/admin/audit-logs"
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1"
                >
                  View all
                  <Eye className="w-4 h-4" />
                </Link>
              </PermissionGate>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                <p className="text-gray-500 dark:text-gray-400">Loading activity...</p>
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="p-8 text-center">
                <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p className="text-gray-500 dark:text-gray-400">No recent activity</p>
              </div>
            ) : (
              recentActivity.map((activity, index) => (
                <div key={index} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="flex items-start">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActivityColor(activity.result)}`}>
                      {renderActivityIcon(activity.action)}
                    </div>
                    
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white">
                        <span className="font-medium">{activity.employee_name || 'Unknown'}</span>
                        {' '}
                        <span className="text-gray-500 dark:text-gray-400">
                          {activity.action.replace(/_/g, ' ')}
                        </span>
                        {activity.resource && (
                          <span className="text-gray-500 dark:text-gray-400">
                            {' '}on {activity.resource}
                          </span>
                        )}
                      </p>
                      <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(activity.timestamp).toLocaleString()}
                      </div>
                    </div>
                    
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      activity.result === 'success'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : activity.result === 'denied'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {activity.result}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Quick Actions
            </h2>
          </div>
          
          <div className="p-6 space-y-3">
            <PermissionGate resource="employees" scope="*" action="create">
              <button
                onClick={() => navigate('/admin/employees/new')}
                className="w-full flex items-center gap-3 px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add Employee</span>
              </button>
            </PermissionGate>
            
            <button
              onClick={() => navigate('/admin/tickets')}
              className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              <Ticket className="w-5 h-5" />
              <span className="font-medium">View Tickets</span>
            </button>
            
            <button
              onClick={() => navigate('/admin/profile')}
              className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">My Profile</span>
            </button>
            
            <PermissionGate resource="audit" scope="logs" action="read">
              <button
                onClick={() => navigate('/admin/audit-logs')}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Activity className="w-5 h-5" />
                <span className="font-medium">View Audit Logs</span>
              </button>
            </PermissionGate>
            
            <PermissionGate resource="security" scope="dashboard" action="read">
              <button
                onClick={() => navigate('/admin/security')}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Shield className="w-5 h-5" />
                <span className="font-medium">Security Dashboard</span>
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedAdminDashboard;
